import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { Loader, ErrorAlert, EmptyRow } from '../components/States.jsx';
import InventoryTable from '../components/InventoryTable.jsx';
import { useGetDashboardQuery } from '../store/apiSlice.js';
import { fmtDate, money } from '../utils/format.js';

function Stat({ label, value, hint, accent }) {
  return (
    <div className={`stat-card${accent ? ' accent' : ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  );
}

/** FRD section 5 — everything the system tracks automatically, on one screen. */
export default function DashboardPage() {
  const { data, isLoading: loading, error: apiError, refetch: reload } = useGetDashboardQuery();
  const error = apiError?.message ?? null;

  if (loading) {
    return (
      <>
        <PageHeader eyebrow="Overview" title="Inventory Dashboard" subtitle="Live stock, purchases and sales." />
        <Card>
          <Loader label="Loading inventory…" />
        </Card>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader eyebrow="Overview" title="Inventory Dashboard" subtitle="Live stock, purchases and sales." />
        <Card>
          <ErrorAlert message={error} onRetry={reload} />
        </Card>
      </>
    );
  }

  const d = data;

  return (
    <>
      <PageHeader
        eyebrow="Overview"
        title="Inventory Dashboard"
        subtitle="Stock, purchases and sales update the moment a Stock In or Stock Out entry is saved."
      />

      <div className="stat-grid">
        <Stat
          accent
          label="Current Available Stock"
          value={d.currentAvailableStock.toLocaleString('en-IN')}
          hint={`across ${d.skuCount} product combination${d.skuCount === 1 ? '' : 's'}`}
        />
        <Stat label="Total Revenue" value={`₹${money(d.totalRevenue)}`} hint={`${d.counts.invoices} invoice(s) raised`} />
        <Stat label="Vendors" value={d.counts.vendors} hint={`${d.counts.products} product names`} />
        <Stat label="Customers" value={d.counts.customers} hint="Standard and Regular" />
      </div>

      <Card title="Available Stock" tag="Received − Sold">
        <InventoryTable defaultView="in" />
      </Card>

      <div className="split-grid" style={{ marginTop: 14 }}>
        <Card title="Stock In History" tag="Latest 10">
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vendor</th>
                  <th>Product</th>
                  <th>Received</th>
                  <th>Stock After</th>
                </tr>
              </thead>
              <tbody>
                {!d.stockInHistory.length ? (
                  <EmptyRow colSpan={5}>Record a delivery in Stock In Management</EmptyRow>
                ) : (
                  d.stockInHistory.map((row) => (
                    <tr key={row.id}>
                      <td className="nowrap">{fmtDate(row.date)}</td>
                      <td>{row.vendor || '—'}</td>
                      <td>
                        <div className="stack-sm">
                          <span>{row.product}</span>
                          <span className="sub-line">
                            {row.texture} · {row.length} · {row.color}
                          </span>
                        </div>
                      </td>
                      <td className="num">{row.qty}</td>
                      <td className="num">{row.totalStock ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Stock Out History" tag="Latest 10">
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Units</th>
                  <th>Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                {!d.stockOutHistory.length ? (
                  <EmptyRow colSpan={5}>Raise an invoice in Stock Out</EmptyRow>
                ) : (
                  d.stockOutHistory.map((row) => (
                    <tr key={row.id}>
                      <td className="nowrap">{fmtDate(row.date)}</td>
                      <td className="nowrap">{row.invoiceNo}</td>
                      <td>{row.customer}</td>
                      <td className="num">{row.units}</td>
                      <td className="num">₹{money(row.grandTotal)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card title="Product-wise Stock Movement" tag="Received vs Sold">
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Received</th>
                <th>Sold</th>
                <th>Available</th>
              </tr>
            </thead>
            <tbody>
              {!d.productWiseMovement.length ? (
                <EmptyRow colSpan={4}>Nothing has moved yet</EmptyRow>
              ) : (
                d.productWiseMovement.map((row) => (
                  <tr key={row.product}>
                    <td>{row.product}</td>
                    <td className="num">{row.qtyIn}</td>
                    <td className="num">{row.qtyOut}</td>
                    <td className="num">{row.available}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="split-grid" style={{ marginTop: 14 }}>
        <Card title="Vendor-wise Purchase History" tag="By units received">
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Entries</th>
                  <th>Units</th>
                  <th>Last Delivery</th>
                </tr>
              </thead>
              <tbody>
                {!d.vendorWisePurchases.length ? (
                  <EmptyRow colSpan={4}>No purchases recorded</EmptyRow>
                ) : (
                  d.vendorWisePurchases.map((row) => (
                    <tr key={row.vendor}>
                      <td>{row.vendor}</td>
                      <td className="num">{row.entries}</td>
                      <td className="num">{row.units}</td>
                      <td className="nowrap">{fmtDate(row.lastDate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Customer-wise Sales History" tag="By revenue">
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Invoices</th>
                  <th>Units</th>
                  <th>Revenue (₹)</th>
                </tr>
              </thead>
              <tbody>
                {!d.customerWiseSales.length ? (
                  <EmptyRow colSpan={4}>No sales recorded</EmptyRow>
                ) : (
                  d.customerWiseSales.map((row) => (
                    <tr key={row.customer}>
                      <td>{row.customer}</td>
                      <td className="num">{row.invoices}</td>
                      <td className="num">{row.units}</td>
                      <td className="num">₹{money(row.revenue)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </>
  );
}